import {
  InMemoryCache,
  type ApolloCache,
  type NormalizedCacheObject,
  type PossibleTypesMap,
  type Reference,
} from '@apollo/client';
import { type ModifierDetails } from '@apollo/client/cache';
import { getCalendarDateTypePolicies } from '@orn/shared-web/lib/apollo/calendarDateTypePolicies';

import { WorkItemStatus } from '../__generated__/globalTypes';
import introspection from '../__generated__/possibleTypes.json';
import { GET_BANK_CONNECTIONS } from '../components/plaid/PlaidConnectionModal';
import { GET_ALL_FINANCIAL_ACCOUNTS, type FinancialAccount } from '../hooks/queries/useFinancialAccounts';

import type { GetBankConnectionsQuery } from '../components/plaid/PlaidConnectionModal/__generated__/PlaidConnectionModal.gql';
import type { GetAllFinancialAccountsQuery } from '../hooks/queries/__generated__/useFinancialAccounts.gql';
import type { FieldMergeFunction } from '@apollo/client';

/**
 * Cursor-guarded connection merge shared by every paginated list field: a
 * cursor-less read is a first page and replaces the cached list, a cursored
 * one appends its edges. One implementation, parameterized by where the
 * field's operation carries its cursor, so the algorithm cannot diverge
 * across fields.
 */
function mergeCursorPaginatedEdges(readCursor: (args: Record<string, unknown> | null) => unknown): FieldMergeFunction {
  return (existing, incoming, { args }) => {
    // If no cursor (first page), replace existing
    if (!readCursor(args)) {
      return incoming;
    }
    // Otherwise, merge edges
    const existingEdges = existing?.edges ?? [];
    const incomingEdges = incoming?.edges ?? [];
    return {
      ...incoming,
      edges: [...existingEdges, ...incomingEdges],
    };
  };
}

const mergeOnPaginationCursor = mergeCursorPaginatedEdges(
  (args) => (args as { pagination?: { cursor?: unknown } } | null)?.pagination?.cursor
);

/**
 * Creates a fresh InMemoryCache configured with the production type policies:
 * - possibleTypes from the introspection file
 * - CalendarDate read functions for Transaction, Bill, BillSubmission, RecurringTransaction
 * - Cursor-based pagination merge for getTransactionsForUser, getWorkItems, getPaidObligationsForUser;
 *   the first two also key on pagination.limit so reads at different depths never overwrite each other
 * - updateWorkItem mutations are explicitly not merged into the cache
 *
 * Each call returns a brand-new cache instance so that different consumers
 * (production Apollo client, Storybook pane provider) can operate on isolated state.
 */
export function createAppCache(): InMemoryCache {
  const possibleTypesMap = introspection.possibleTypes as unknown as PossibleTypesMap;
  return new InMemoryCache({
    possibleTypes: possibleTypesMap,
    typePolicies: {
      ...getCalendarDateTypePolicies(),
      Query: {
        fields: {
          getTransactionsForUser: {
            // Depth is in the key so a short read (a record page's three rows)
            // cannot replace a table's deeper list under the same filters.
            keyArgs: ['filters', 'sortKey', 'pagination', ['limit']],
            merge: mergeOnPaginationCursor,
          },
          getPaidObligationsForUser: {
            keyArgs: false,
            // The one field whose operation carries its cursor under a
            // different argument name.
            merge: mergeCursorPaginatedEdges(
              (args) => (args as { paidPagination?: { cursor?: unknown } } | null)?.paidPagination?.cursor
            ),
          },
          getWorkItems: {
            // Depth is in the key so a read at one limit cannot overwrite a
            // deeper one — see `docs/work-item-alert-reads.md`. The store
            // field's *name* stays `getWorkItems`, so `evictWorkItemReads`
            // still drops every depth in one call.
            keyArgs: ['filters', 'pagination', ['limit']],
            merge: mergeOnPaginationCursor,
          },
          currentSubscription: {
            // HouseholdSubscription carries no id, and two queries select
            // disjoint slices (the billing card's status fields; the switch
            // screen's switchTerms). Without a merge the later write replaces
            // the object and drops the other query's slice from the cache.
            merge: true,
          },
        },
      },
      Mutation: {
        fields: {
          updateWorkItem: {
            merge: false,
          },
        },
      },
    },
  });
}

/**
 * Drop every cached work-item read so the next render refetches.
 *
 * Deliberately an evict rather than a surgical edge filter: archiving moves a
 * row between the live and archived lists in opposite directions while the
 * counts move with it, so a filter that only ever removes gets one of those
 * wrong. Evicting by field name clears every filter set and every window depth.
 *
 * Exported for the push path: `useWorkItemLiveUpdates` evicts through this same
 * seam when a delivered event implies a membership change, so the push and
 * mutation paths cannot drift on which reads a change invalidates. Typed
 * `ApolloCache<object>` because that caller reads the cache off
 * `useApolloClient()`, which erases the serialized shape; eviction never
 * touches it.
 */
export function evictWorkItemReads(cache: ApolloCache<object>) {
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'getWorkItems' });
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'getWorkItemStatusCounts' });
}

/**
 * Handle cache updates when financial accounts are created (via exchangePublicToken or createFinancialAccounts).
 * Merges new accounts into the existing cache and updates bank connections.
 */
function handleFinancialAccountsUpdate(
  cache: ApolloCache<NormalizedCacheObject>,
  data: {
    exchangePublicToken?: FinancialAccount[];
    createFinancialAccounts?: FinancialAccount[];
  }
) {
  const newAccounts = data?.exchangePublicToken ?? data?.createFinancialAccounts;
  if (!newAccounts?.length) {
    return;
  }

  // Update financial accounts cache
  const existingAccountsData = cache.readQuery<GetAllFinancialAccountsQuery>({
    query: GET_ALL_FINANCIAL_ACCOUNTS,
  });

  const combinedAccounts = [...(existingAccountsData?.getFinancialAccountsForUser ?? []), ...newAccounts];

  // Deduplicate by id, keeping the last occurrence (newer data)
  const uniqueAccounts = Array.from(new Map(combinedAccounts.map((account) => [account.id, account])).values());

  cache.writeQuery<GetAllFinancialAccountsQuery>({
    query: GET_ALL_FINANCIAL_ACCOUNTS,
    data: {
      getFinancialAccountsForUser: uniqueAccounts,
    },
  });

  // Update bank connections cache
  const existingBankConnectionsData = cache.readQuery<GetBankConnectionsQuery>({
    query: GET_BANK_CONNECTIONS,
  });

  // Extract unique bank connections from new accounts
  const newBankConnections = Array.from(
    new Map(
      newAccounts.map((account) => [
        account.bankConnection.id,
        {
          __typename: 'BankConnection' as const,
          id: account.bankConnection.id,
          bank: account.bank,
        },
      ])
    ).values()
  );

  const combinedBankConnections = [
    ...(existingBankConnectionsData?.getBankConnectionsForUser ?? []),
    ...newBankConnections,
  ];

  // Deduplicate by id, keeping the last occurrence (newer data)
  const uniqueBankConnections = Array.from(
    new Map(combinedBankConnections.map((connection) => [connection.id, connection])).values()
  );

  cache.writeQuery<GetBankConnectionsQuery>({
    query: GET_BANK_CONNECTIONS,
    data: {
      getBankConnectionsForUser: uniqueBankConnections,
    },
  });
}

/**
 * Handle cache updates when a connection's health work items are archived.
 * Invalidates the work-item reads so the lists and counts refetch — the
 * account-link success path resolves the reconnect condition behind them.
 */
function handleArchiveBankConnectionWorkItems(
  cache: ApolloCache<NormalizedCacheObject>,
  data: { archiveBankConnectionWorkItems?: string[] }
) {
  if (!data?.archiveBankConnectionWorkItems?.length) {
    return;
  }

  evictWorkItemReads(cache);
}

/**
 * Drop only the work-item counts. The list is deliberately left alone: a read
 * flip cannot cross a list boundary (`getWorkItems` filters on `archived`,
 * never `read`), and evicting the list collapses the Center's loaded pages —
 * the regression the mark-read path litigated once already. Exported for the
 * push path, which uses it for deliveries that change no membership; typed
 * `ApolloCache<object>` for the same reason as {@link evictWorkItemReads}.
 */
export function evictWorkItemCounts(cache: ApolloCache<object>) {
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'getWorkItemStatusCounts' });
}

/** The record types that carry a `workItem` chip the sync keeps honest. */
type ChippedTypename = 'Transaction' | 'Bill' | 'RecurringTransaction';

const CHIPPED_TYPENAMES: readonly ChippedTypename[] = ['Transaction', 'Bill', 'RecurringTransaction'];

interface IChippedRecord {
  id: string;
  /**
   * The record's cached stream as a normalized ref — the second place a bill's
   * item can come from. `null` is a record with no stream; `undefined` is one
   * whose read never selected it, so whether the stream holds the item is unknown.
   */
  recurringTransactionRef: string | null | undefined;
}

type NormalizedStore = Record<
  string,
  {
    __typename?: string;
    id?: string;
    workItem?: { __ref?: string } | null;
    recurringTransaction?: { __ref?: string } | null;
  }
>;

/**
 * Every record of `typename` whose cached `workItem` chip points at this item.
 *
 * The normalized store is the only record of the *prior* links: a write can
 * replace the item's links outright, and neither a mutation response nor a
 * subscription payload says which links it took away — both name only what the
 * item points at now. Reading the back-references recovers the difference
 * without the caller having to have watched the item's whole history.
 *
 * Deliberately a scan of one `extract()` rather than a maintained reverse
 * index: this runs on a debounced write, never on a render, and the
 * alternative is cache bookkeeping that can itself go stale.
 */
function findRecordsChippedTo(store: NormalizedStore, typename: ChippedTypename, workItemId: string): IChippedRecord[] {
  const chipRef = `WorkItem:${workItemId}`;
  return Object.values(store)
    .filter((entity) => entity?.__typename === typename && entity.workItem?.__ref === chipRef)
    .flatMap((entity) =>
      typeof entity.id === 'string'
        ? [
            {
              id: entity.id,
              recurringTransactionRef:
                'recurringTransaction' in entity ? (entity.recurringTransaction?.__ref ?? null) : undefined,
            },
          ]
        : []
    );
}

/** The links a work-item write reports, as the mutation result and the push payload both carry them. */
export interface IWorkItemChipLinks {
  id: string;
  archived: boolean;
  transactionIds: readonly string[];
  /** Absent from a payload that did not select the bills, which leaves the bill chips untouched short of an archive. */
  billIds?: readonly string[];
  /** Absent from a payload that did not select the stream, which leaves the stream chips untouched short of an archive. */
  recurringTransactionId?: string | null;
}

/**
 * Point each linked record's `workItem` chip at the item, or clear it when the
 * item is archived — an archived row no longer hangs off its records, and an
 * unarchive restores the link without waiting for an unrelated refetch.
 * Shared by the mutation path ({@link handleUpdateWorkItem}) and the push path
 * (`useWorkItemLiveUpdates`), so a chip stays honest no matter which session's
 * write moved the item.
 *
 * An archive clears every chip pointing at the item, read from the cache's own
 * back-references ({@link findRecordsChippedTo}) rather than from the payload:
 * the row hangs off nothing afterward, whatever the payload selected. After
 * that, the record read would answer with the next-newest live item, if any,
 * while the cache says `null` until it re-runs — an accepted edge.
 *
 * Chips a live write took *away* are cleared from the same back-references,
 * since a payload cannot express a removal. Without that, re-pointing an item
 * from one transaction or bill to another leaves the first one's chip claiming
 * a link that no longer exists until an unrelated refetch. A bill is the one
 * record with two ways to hold an item — its own link, else its stream's — so
 * a bill the payload no longer names keeps its chip while its cached stream is
 * the item's; a mark-read echo on a stream-raised item names no bills at all.
 * The stream link itself is single-valued and set when the item is raised, so
 * the stream chip is only ever set. A payload that never selected the bills or
 * the stream leaves those chips alone rather than reading its silence as an
 * unlink; the mirror case, an item raised against a stream reaching a cached
 * bill page, waits for that page's next read. A set onto a chip that names a
 * different item never simply overwrites it: a transaction or stream chip is
 * ranked ({@link displacesChip}), and a bill chip is dropped so the page
 * re-reads.
 */
export function syncWorkItemChips(cache: ApolloCache<object>, workItem: IWorkItemChipLinks) {
  const store = cache.extract() as NormalizedStore;
  const clearChip = (typename: ChippedTypename, id: string) => {
    cache.modify({ id: cache.identify({ __typename: typename, id }), fields: { workItem: () => null } });
  };

  if (workItem.archived) {
    for (const typename of CHIPPED_TYPENAMES) {
      for (const record of findRecordsChippedTo(store, typename, workItem.id)) {
        clearChip(typename, record.id);
      }
    }
    return;
  }

  const currentTransactions = new Set(workItem.transactionIds);
  for (const record of findRecordsChippedTo(store, 'Transaction', workItem.id)) {
    if (!currentTransactions.has(record.id)) {
      clearChip('Transaction', record.id);
    }
  }

  const links: Array<{ typename: ChippedTypename; id: string }> = workItem.transactionIds.map((id) => ({
    typename: 'Transaction',
    id,
  }));
  // The bill diff needs the stream as well: without it, a bill held through
  // its stream reads as unlinked.
  if (workItem.billIds !== undefined && workItem.recurringTransactionId !== undefined) {
    const currentBills = new Set(workItem.billIds);
    const streamRef = workItem.recurringTransactionId
      ? cache.identify({ __typename: 'RecurringTransaction', id: workItem.recurringTransactionId })
      : null;
    for (const record of findRecordsChippedTo(store, 'Bill', workItem.id)) {
      // A bill whose read never selected its stream cannot be told apart from
      // one held through it, so it keeps its chip.
      const heldThroughStream =
        record.recurringTransactionRef === undefined ||
        (streamRef !== null && record.recurringTransactionRef === streamRef);
      if (!currentBills.has(record.id) && !heldThroughStream) {
        clearChip('Bill', record.id);
      }
    }
    links.push(...workItem.billIds.map((id) => ({ typename: 'Bill' as const, id })));
  }
  if (workItem.recurringTransactionId) {
    links.push({ typename: 'RecurringTransaction', id: workItem.recurringTransactionId });
  }
  for (const link of links) {
    cache.modify({
      id: cache.identify({ __typename: link.typename, id: link.id }),
      fields: {
        workItem: (existing: Reference | null | undefined, { readField, toReference, DELETE }) => {
          const incoming = toReference({ __typename: 'WorkItem', id: workItem.id }) ?? null;
          if (!existing || !incoming || readField<string>('id', existing) === workItem.id) {
            return incoming;
          }
          // A bill reads its own link before its stream's, an order the cache
          // cannot reproduce; dropping the field makes a mounted page re-read,
          // and it keeps its last result on screen until that read lands.
          if (link.typename === 'Bill') {
            return DELETE;
          }
          return displacesChip(existing, incoming, readField) ? incoming : existing;
        },
      },
    });
  }
}

/**
 * Whether an item may take a transaction's or stream's chip that points at a
 * different item.
 *
 * Both of those reads answer with the record's newest unarchived item, so a
 * set mirrors that order rather than overwriting: an archived chip is always
 * displaced, a live one only by a newer item (ties broken by id, as the read
 * breaks them), and a chip whose age the cache does not hold stays as the read
 * left it. Without this, unarchiving an older item that names the same record
 * would hide the newer one until the record re-reads. A bill's read has a
 * second arm, so its conflicting chip is dropped instead ({@link syncWorkItemChips}).
 */
function displacesChip(existing: Reference, incoming: Reference, readField: ModifierDetails['readField']): boolean {
  if (readField<WorkItemStatus>('status', existing) === WorkItemStatus.Archived) {
    return true;
  }
  const existingCreatedAt = readField<string>('createdAt', existing);
  const incomingCreatedAt = readField<string>('createdAt', incoming);
  if (existingCreatedAt === undefined || incomingCreatedAt === undefined) {
    return false;
  }
  if (incomingCreatedAt !== existingCreatedAt) {
    return new Date(incomingCreatedAt).getTime() > new Date(existingCreatedAt).getTime();
  }
  return (readField<string>('id', incoming) ?? '') > (readField<string>('id', existing) ?? '');
}

/**
 * Handle cache updates when work items are marked read. Only the counts are
 * dropped: `getWorkItems` filters on `archived`, never on `read`, so a
 * read flip cannot cross a list boundary by construction. The mutation returns
 * each flipped row's `read`, which Apollo writes onto the normalized
 * `WorkItem`, so the row's badge updates with no refetch. An empty result —
 * every named row was already read — moved nothing, so nothing is dropped.
 */
function handleMarkWorkItemsRead(
  cache: ApolloCache<NormalizedCacheObject>,
  data: { markWorkItemsRead?: Array<{ id: string }> }
) {
  if (!data?.markWorkItemsRead?.length) {
    return;
  }

  evictWorkItemCounts(cache);
}

/**
 * Handle cache updates when a work item is archived or unarchived.
 *
 * Keyed on the field's *presence*, not its value: `updateWorkItem` answers
 * `null` for a row that is unexposed or no longer visible to the caller, and
 * that phantom is exactly the row that must leave the screen — skipping the
 * evict on `null` would keep it there until a reload.
 *
 * An archived row also no longer hangs off its records — the `workItem` chip
 * on each linked transaction, bill, and stream is cleared the same way — and
 * an unarchive restores the links, so the chips return without waiting for an
 * unrelated refetch. The `null` phantom carries no links, so its chips go
 * stale until an unrelated refetch — an accepted edge.
 */
function handleUpdateWorkItem(cache: ApolloCache<NormalizedCacheObject>, data: IMutationResultData) {
  if (!('updateWorkItem' in data)) {
    return;
  }

  evictWorkItemReads(cache);

  const updateWorkItem = data.updateWorkItem;
  if (!updateWorkItem) {
    return;
  }

  syncWorkItemChips(cache, {
    id: updateWorkItem.id,
    archived: updateWorkItem.status === WorkItemStatus.Archived,
    transactionIds: (updateWorkItem.transactions ?? []).map((transaction) => transaction.id),
    billIds: updateWorkItem.bills?.map((bill) => bill.id),
    recurringTransactionId: updateWorkItem.recurringTransaction?.id ?? null,
  });
}

/**
 * Handle cache updates when the phone number is verified. Verification
 * resolves the unverified-phone CONFIG alert server-side, so the reads refetch
 * rather than keeping the row on screen.
 */
function handleVerifyPhoneNumber(
  cache: ApolloCache<NormalizedCacheObject>,
  data: { verifyPhoneNumber?: { id: string } | null }
) {
  if (!data?.verifyPhoneNumber) {
    return;
  }

  evictWorkItemReads(cache);
}

/**
 * Handle cache updates when the user's profile changes. A profile edit can
 * resolve or raise a CONFIG alert (e.g. the phone number changing arms
 * verification), so the reads refetch.
 */
function handleUpdateUser(cache: ApolloCache<NormalizedCacheObject>, data: { updateUser?: { id: string } | null }) {
  if (!data?.updateUser) {
    return;
  }

  evictWorkItemReads(cache);
}

/**
 * Handle cache updates when a financial account is soft-deleted.
 * Removes the account from the accounts list, evicts its entity, and evicts the
 * root fields whose server results change shape with it (transaction feeds, the
 * connections list — its nested accounts shrink — and work items, which the
 * server resolves as part of the delete) so they refetch cleanly.
 */
function handleDeleteFinancialAccount(
  cache: ApolloCache<NormalizedCacheObject>,
  data: { deleteFinancialAccount?: string }
) {
  const deletedAccountId = data?.deleteFinancialAccount;
  if (!deletedAccountId) {
    return;
  }

  cache.modify({
    fields: {
      getFinancialAccountsForUser(existingAccounts, { readField }) {
        if (!Array.isArray(existingAccounts)) {
          return existingAccounts;
        }
        return existingAccounts.filter((accountRef) => readField('id', accountRef as Reference) !== deletedAccountId);
      },
    },
  });

  cache.evict({ id: cache.identify({ __typename: 'FinancialAccount', id: deletedAccountId }) });
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'getTransactionsForUser' });
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'getAllTransactionsForUser' });
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'hasTransactionsForUser' });
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'getBankConnectionsForUser' });
  evictWorkItemReads(cache);
  cache.gc();
}

interface IMutationResultData {
  markWorkItemsRead?: Array<{ id: string }>;
  updateWorkItem?: {
    id: string;
    status?: WorkItemStatus;
    transactions?: Array<{ id: string }>;
    bills?: Array<{ id: string }>;
    recurringTransaction?: { id: string } | null;
  } | null;
  verifyPhoneNumber?: { id: string } | null;
  updateUser?: { id: string } | null;
  exchangePublicToken?: FinancialAccount[];
  createFinancialAccounts?: FinancialAccount[];
  archiveBankConnectionWorkItems?: string[];
  deleteFinancialAccount?: string;
}

/**
 * Builds the centralized mutation update handler that composes cache updates
 * for work-item status changes, financial account creation, and account deletion.
 *
 * Wire this into ApolloClient's defaultOptions.mutate.update so every mutation
 * automatically flows through all handlers.
 */
export function createMutationUpdateHandler() {
  return (cache: ApolloCache<NormalizedCacheObject>, { data }: { data?: unknown }) => {
    const safeData = (data ?? {}) as IMutationResultData;
    handleMarkWorkItemsRead(cache, safeData);
    handleUpdateWorkItem(cache, safeData);
    handleVerifyPhoneNumber(cache, safeData);
    handleUpdateUser(cache, safeData);
    handleFinancialAccountsUpdate(cache, safeData);
    handleArchiveBankConnectionWorkItems(cache, safeData);
    handleDeleteFinancialAccount(cache, safeData);
  };
}
